import { FACEBOOK_TRACKER_NAME } from '../../constants';
import { loadScript } from '@buzzfeed/bf-utils/lib/load-script';
import { logTrackingEvents } from '../utils';
import cookies from '@buzzfeed/bf-utils/lib/cookies';
import { framework, rules } from '@buzzfeed/consent';

export class FacebookTracker {

  constructor({ trackingId, cluster }) {
    this.trackingId = trackingId;
    this.cluster = cluster;

    if (typeof window === 'undefined') {
      return {
        trackEvent: () => {},
        _loadFacebookScript: () => {},
        _getFacebookTracker: () => {}
      };
    }
  }

  async _loadFacebookScript() {
    // if we are not in prod, don't actually call the script - load as a mock logger instead
    // This will not log LinkClick or other events set up by the permutive script itself
    if (this.cluster !== 'prod') {
      window[FACEBOOK_TRACKER_NAME] = (eventName, eventType, eventData) => logTrackingEvents('Facebook', { eventName, eventType, eventData });
      window[FACEBOOK_TRACKER_NAME].loaded = true;
      return true;
    }

    // async version of the FB init script
    try {
      await (async function(f, b, e, v, n) {
        if (f.fbq) {
          return;
        }
        n = f.fbq = function() {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) {
          f._fbq = n;
        }
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        await loadScript(v);
      })(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js'
      );

      // add permutive id to init event for Facebook Value Optimization
      // https://www.facebook.com/business/help/296463804090290?id=561906377587030
      let permutiveId = cookies.get('permutive-id') || false;
      let initData = {};
      if (permutiveId) {
        initData['external_id'] = permutiveId;
      }

      // set Limited Data Use options for CCPA compliance
      // https://developers.facebook.com/docs/marketing-apis/data-processing-options
      let ccpaOptOut = await rules.fetchCCPAOptOut();
      if (ccpaOptOut) {
        window[FACEBOOK_TRACKER_NAME]('dataProcessingOptions', ['LDU'], 1, 1000);
      } else {
        window[FACEBOOK_TRACKER_NAME]('dataProcessingOptions', []);
      }

      window[FACEBOOK_TRACKER_NAME]('init', this.trackingId, initData);
      return true;
    } catch {
      // @todo - logging for FB load fail
      return false;
    }
  }

  async _getFacebookTracker() {
    if (window[FACEBOOK_TRACKER_NAME] && window[FACEBOOK_TRACKER_NAME].loaded) {
      return window[FACEBOOK_TRACKER_NAME];
    }

    // check to see if we have consent before we load anything
    const needsConsent = framework.needsConsent();
    let hasTrackingConsent;

    if (!needsConsent) {
      await this._loadFacebookScript();
    } else {
      try {
        hasTrackingConsent = await rules.hasConsented('tracking');
        if (hasTrackingConsent) {
          await this._loadFacebookScript();
        }
      } catch {
        // @todo - logging for FB load fail
      }
    }

    // if we've made it this far, we don't have consent or couldn't load the script
    // load mock fns to prevent app error, but don't log anything out
    if (!window[FACEBOOK_TRACKER_NAME]) {
      window[FACEBOOK_TRACKER_NAME] = () => {};

      // if we explicitly do not have consent, mock config so we don't try to load again for this page
      // otherwise, we will retry load the next time this function is called
      if (typeof hasTrackingConsent !== 'undefined' && !hasTrackingConsent) {
        window[FACEBOOK_TRACKER_NAME].loaded = true;
      }
    }

    return window[FACEBOOK_TRACKER_NAME];
  }

  async trackEvent({ eventType, eventData, options }) {
    const tracker = await this._getFacebookTracker();

    if (options && options.custom) {
      tracker('trackCustom', eventType, eventData || {});
    } else {
      tracker('track', eventType, eventData || {});
    }
  }

}
